/**
 * Native scroll-slider. Geen bibliotheek: het thema doet desktop-sliders met Swiper,
 * maar de opdracht verbiedt dat, en scroll-snap is hier ook eerlijker - vegen op
 * touch en scrollen met het wiel werken dan vanzelf.
 *
 * De knoppen mogen overal binnen het element staan, ook naast de kop, zolang ze
 * data-scroller-prev of data-scroller-next hebben.
 *
 * Zonder JavaScript blijft dit een gewone scrollbare rij; de knoppen worden dan
 * door de CSS verborgen.
 */
if (!customElements.get('ledge-scroller')) {
  class LedgeScroller extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-scroller-viewport]');
      if (!this.viewport) return;

      this.prev = this.querySelector('[data-scroller-prev]');
      this.next = this.querySelector('[data-scroller-next]');
      this.controls = this.querySelector('[data-scroller-controls]');

      // Live uitlezen, niet FoxTheme.config.motionReduced: die wordt één keer bij
      // page load bepaald en volgt een wijziging tijdens de sessie niet.
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.update = this.update.bind(this);

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeydown);
      this.prev?.addEventListener('click', () => this.scrollByCard(-1));
      this.next?.addEventListener('click', () => this.scrollByCard(1));

      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.update);
        this.observer.observe(this.viewport);
      }

      // Inhoud die erbij komt of wegvalt verandert alleen de scrollbreedte, niet de
      // maat van de track zelf - een ResizeObserver ziet dat dus niet. Vandaar een
      // MutationObserver op de kinderen, zodat de pijlknoppen meteen kloppen als er
      // in de theme editor een blok bij komt.
      if (window.MutationObserver && this.viewport.firstElementChild) {
        this.mutations = new MutationObserver(this.update);
        this.mutations.observe(this.viewport.firstElementChild, { childList: true });
      }
      document.fonts?.ready.then(this.update);

      this.update();
      this.setupAutoplay();
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('keydown', this.onKeydown);
      this.observer?.disconnect();
      this.mutations?.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.stopAutoplay();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /**
     * Automatisch doorschuiven. Aan het einde springt hij terug naar het begin.
     *
     * Over toegankelijkheid: bewegende inhoud heeft een manier nodig om te stoppen.
     * Die zit hier drie keer in - hij pauzeert bij hover en bij focus, hij staat stil
     * zolang het tabblad op de achtergrond is, en zodra iemand zelf een pijlknop
     * gebruikt of veegt stopt hij definitief. Bij prefers-reduced-motion begint hij
     * niet eens.
     */
    setupAutoplay() {
      const interval = parseInt(this.dataset.autoplay, 10);
      if (!interval || this.reduceMotion.matches) return;

      this.autoplayInterval = interval;
      this.autoplayStopped = false;

      this.addEventListener('pointerenter', () => this.pauseAutoplay());
      this.addEventListener('pointerleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      });

      // Zelf bedienen wint: dan houdt hij er definitief mee op.
      this.prev?.addEventListener('click', () => this.stopAutoplay(true));
      this.next?.addEventListener('click', () => this.stopAutoplay(true));
      this.viewport.addEventListener('pointerdown', () => this.stopAutoplay(true));

      this.onVisibility = () => {
        if (document.hidden) this.pauseAutoplay();
        else this.startAutoplay();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.startAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayInterval || this.autoplayStopped || this.timer) return;
      this.timer = setInterval(() => this.advance(), this.autoplayInterval);
    }

    pauseAutoplay() {
      clearInterval(this.timer);
      this.timer = null;
    }

    stopAutoplay(permanent) {
      this.pauseAutoplay();
      if (permanent) this.autoplayStopped = true;
    }

    advance() {
      const el = this.viewport;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 2) return;
      if (Math.abs(el.scrollLeft) >= max - 2) {
        el.scrollTo({ left: 0, behavior: this.reduceMotion.matches ? 'instant' : 'smooth' });
      } else {
        this.scrollByCard(1);
      }
    }

    get items() {
      return this.viewport.firstElementChild?.children ?? [];
    }

    /**
     * Eén kaart plus de tussenruimte, gemeten aan het verschil tussen de eerste twee
     * kaarten. Niet berekend uit CSS-variabelen: zo klopt het ook bij subpixels, een
     * andere gap per breekpunt, en RTL.
     */
    get step() {
      const items = this.items;
      if (items.length === 0) return this.viewport.clientWidth;
      if (items.length === 1) return items[0].getBoundingClientRect().width;
      return (
        Math.abs(
          items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
        ) || items[0].getBoundingClientRect().width
      );
    }

    scrollByCard(direction) {
      const sign = FoxTheme.config.isRTL ? -1 : 1;
      this.viewport.scrollBy({
        left: this.step * direction * sign,
        // 'instant', niet 'auto': 'auto' betekent "volg scroll-behavior uit de CSS",
        // en dan zou het alsnog vloeiend scrollen.
        behavior: this.reduceMotion.matches ? 'instant' : 'smooth',
      });
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    }

    onKeydown(event) {
      // Alleen als de scroller zelf de focus heeft. Staat de focus op een link in een
      // kaart, dan horen de pijltjes daarbij en niet bij ons.
      if (event.target !== this.viewport) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.scrollByCard(event.key === 'ArrowRight' ? 1 : -1);
    }

    update() {
      const el = this.viewport;
      const max = el.scrollWidth - el.clientWidth;
      const x = Math.abs(el.scrollLeft);

      // Tolerantie van 2px: met scroll-snap en gebroken kaartbreedtes haalt scrollLeft
      // zelden exact 0 of exact max, en dan gaat de knop nooit uit.
      const scrollable = max > 2;

      this.classList.toggle('ledge-scroller--no-overflow', !scrollable);
      if (this.controls) this.controls.hidden = !scrollable;

      // Een gebied waar niets te scrollen valt hoort niet in de taborde.
      if (scrollable) {
        el.setAttribute('tabindex', '0');
      } else {
        el.removeAttribute('tabindex');
      }

      if (this.prev) this.prev.disabled = !scrollable || x <= 2;
      if (this.next) this.next.disabled = !scrollable || x >= max - 2;
    }
  }

  customElements.define('ledge-scroller', LedgeScroller);
}
